import { DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React, { useState } from 'react';
import {
    emailList,
    maxLength,
    minLength,
    minPhoneLength,
    required,
    Validators,
} from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import styles from '../assets/password-recovery-screen.module.scss';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import { asyncPersistIntakeOfficePolicies } from '../../../../../store/actions/intakeSent-actions';
import { push } from 'connected-react-router';
import Router from '../../../../../navigation/router';
import { routes } from '../../../../../navigation';

export interface OfficePoliciesFormData {
    student: string;
    formId: string;
    isForAll: boolean;
    isTakeReasonable: boolean;
    isConfidentiality: boolean;
    isEmergency: boolean;
    isTestingInclude: boolean;
    fullName: string;
    ssnFour: string | null;
    todayDate: string | null;
}

interface OfficePoliciesFormProps {
    initialValues: any;
    setIsLoader: any;
}

type InjectedProps = InjectedFormProps<OfficePoliciesFormData, OfficePoliciesFormProps>;

const validators: Validators<OfficePoliciesFormData> = {
    isForAll: [required({ message: "It's required." })],
    isTakeReasonable: [required({ message: "It's required." })],
    isConfidentiality: [required({ message: "It's required." })],
    isEmergency: [required({ message: "It's required." })],
    isTestingInclude: [required({ message: "It's required." })],
    fullName: [required({ message: 'Full Name is required.' }), maxLength({ max: 255 })],
    ssnFour: [required({ message: 'Last 4 Digits of the SSN is required.' }), minLength({ min: 4 })],
    todayDate: [required({ message: 'Date is required.' }), maxLength({ max: 255 })],
};

const OfficePoliciesForm: React.FunctionComponent<InjectedProps & OfficePoliciesFormProps> = ({
                                                                                                  handleSubmit,
                                                                                                  error,
                                                                                                  dirty,
                                                                                                  submitting,
                                                                                                  initialValues,
                                                                                                  setIsLoader,
                                                                                              }: InjectedProps & OfficePoliciesFormProps) => {
    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <h2>MPG: Office Policies</h2>
            <div>Please check the box next to each paragraph to provide your consent to the above terms.</div>
            <FormError error={error} />
            <FieldInput
                name="student"
                type="hidden"
                wrapperConfig={{
                    label: '',
                }}
            />
            <FieldInput
                name="formId"
                type="hidden"
                value={6}
                wrapperConfig={{
                    label: '',
                }}
            />

            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    For all in-office, in-home or telehealth sessions, there is a 48-hour cancellation policy, barring emergencies or last-minute sickness.
                </div>
                <FieldInput
                    name="isForAll"
                    type="checkbox"
                    validate={validators.isForAll}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
                <div className={classNames(styles.fixNotice)}>
                    Notice should be emailed to <a href="mailto: Info@manhattanpsychologygoup.com">Info@manhattanpsychologygroup.com</a> or given by
                    telephone at <a href="tel:646-389-4112">646-389-4112</a>.
                </div>
            </div>
            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    MPG takes reasonable precautions in following Health Information Privacy recommendations per the U.S. Department of Health & Human Services in the use of unencrypted email to communicate with patients, such as sending receipts, scheduling appointments and coordinating care with collateral providers. Also, I may receive marketing or promotional emails from MPG which I can "opt-out" of at any time.
                </div>
                <FieldInput
                    name="isTakeReasonable"
                    type="checkbox"
                    validate={validators.isTakeReasonable}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>
            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    Confidentiality & Sharing Information Treatment from MPG is confidential and requests to share patient health information with those outside of MPG require written release. Written release is not required, however, to share patient information internally between MPG staff. A request in writing can be given that information not be shared within MPG. MPG is obligated by law to break confidentiality if the patient is a danger to themselves or others, under court order or when there is suspicion of child abuse.
                </div>
                <FieldInput
                    name="isConfidentiality"
                    type="checkbox"
                    validate={validators.isConfidentiality}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>
            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    Emergencies In the event of an emergency, patients should call 911 and/or go to their nearest emergency room. Patients should not contact MPG regarding an emergency, as MPG does not provide 24-hour patient coverage.
                </div>
                <FieldInput
                    name="isEmergency"
                    type="checkbox"
                    validate={validators.isEmergency}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>
            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    Testing includes: intake interview, observation, in-person and/or remote testing including rating scales, and feedback. A written report is provided. Authorization to exchange information will be requested so that the evaluator can speak with collaterals (i.e. teachers, service providers, etc.). If testing is prematurely terminated, MPG may be unable to provide ANY feedback about the test results or a written report.
                </div>
                <FieldInput
                    name="isTestingInclude"
                    type="checkbox"
                    validate={validators.isTestingInclude}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>


            <div className={classNames(styles.textBox)}>
                By typing my full name below and the last 4 digits of my SSN, I verify that I have read and understand
                the terms of treatment with MPG outlined above.
            </div>

            <FieldInput
                name="fullName"
                type="text"
                validate={validators.fullName}
                wrapperConfig={{
                    label: 'Full Name: *',
                }}
            />
            <FieldInput
                name="ssnFour"
                type="text"
                validate={validators.ssnFour}
                wrapperConfig={{
                    label: 'Last 4 Digits of the SSN *',
                }}
            />

            <FieldDate
                name="todayDate"
                type="text"
                date={true}
                validate={validators.todayDate}
                wrapperConfig={{
                    label: "Today's date *",
                }}
            />

            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> Confirm and Send
                </button>
            </div>
        </form>
    );
};

export default reduxForm<OfficePoliciesFormData, OfficePoliciesFormProps>({
    form: 'office-policies-form',
    enableReinitialize: true,
    onSubmit: async (data: OfficePoliciesFormData, dispatch: Dispatch<any>, props: OfficePoliciesFormProps) => {
        props.setIsLoader(true);
        await asyncPersistIntakeOfficePolicies({ ...data }, dispatch);
    },
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<OfficePoliciesFormData, OfficePoliciesFormProps, string>,
    ) {
        props.setIsLoader(false);
        //toast.success(`The form was successfully sent.`);
        dispatch(push(Router.generate(routes.INTAKE_PARENT_FORM_SUCCESS, {})));
    },

    onSubmitFail(errors: { _error: string }) {
        // props.setIsLoader(false);
        if (errors) {
            let field: any = null;
            Object.keys(errors).map((e: any, index: number, array: any) => {
                if (!field) {
                    field = e;
                    return;
                }
                return;
            });
            if (field) {
                const section = document.querySelector(`label[for='${field}']`);
                section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(OfficePoliciesForm);
